import React, { useState } from "react";
import { Button } from "antd";
import { DeleteOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import BaseTable from "../../../../components/BaseTable";
import Column from "../../../../components/Column";
import Row from "../../../../components/Row";
import ActionHeader from "../../../../components/ActionHeader";
import usePageState from "../../../../hooks/usePageState";
import useConfirmAction from "../../../../hooks/useConfirmAction";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import { useDateRangeSelector } from "../../../../hooks/useDateRangeSelector";
import { currencyFormat } from "../../../../../applications/utils/NumberUtils";
import { DATE_RANGE_OPTION_ENUM } from "../../../../../applications/constants/AppConst";
import {
  FilterInventoryAuditRequest,
  InventoryAuditDetailModel,
  InventoryAuditModel,
} from "../../../../../infrastructure/model/InventoryAuditModel";
import {
  deleteInventoryAuditUseCase,
  getInventoryAuditDetailUseCase,
  getInventoryAuditUseCase,
} from "../../../../../applications/useCase/ApiUseCase/InventoryAuditApiUseCase";
import Table, { ColumnsType } from "antd/es/table";

interface InventoryCheckHistoryProps {
  activeTab: string;
}

function InventoryCheckHistory(props: InventoryCheckHistoryProps) {
  const { activeTab } = props;

  const [dataTable, setDataTable] = useState<InventoryAuditModel[]>([]);
  const [itemSelected, setItemSelected] = useState<InventoryAuditModel>();
  const { dialog: confirmDeleteDialog, confirm: confirmDelete } = useConfirmAction({
    onConfirm: deleteInventoryAudit,
  });
  const { isLoading, setLoading, showError, showSuccess, showClientError } = usePageState();
  const { endDate, startDate, DateRangeSelectorComponent, validateDateRange } =
    useDateRangeSelector({
      initSelectOptionDate: DATE_RANGE_OPTION_ENUM.CUSTOM,
    });
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const [filter, setFilter] = useState<FilterInventoryAuditRequest>({
    page: 1,
    limit: 50,
    end_date: endDate!,
    start_date: startDate!,
  });
  const [total, setTotal] = useState<number>(0);

  async function getListData() {
    if (!validateDateRange) return;
    const dataRes = await getInventoryAuditUseCase({
      request: {
        ...filter,
        start_date: startDate!,
        end_date: endDate!,
      },
      setLoading,
      showError,
    });
    setDataTable(dataRes.data);
    setTotal(dataRes.total);
    setItemSelected(undefined);
  }

  async function deleteInventoryAudit() {
    if (!itemSelected?.id) return;
    const dataRes = await deleteInventoryAuditUseCase({
      id: itemSelected.id,
      setLoading,
      showError,
      showSuccess,
    });
    if (dataRes) {
      setDataTable(dataTable.filter((e) => e.id !== itemSelected.id));
      setItemSelected(undefined);
    }
  }

  async function getInventoryAuditDetail(auditId: number) {
    const indexUpdate = dataTable.findIndex((e) => e.id === auditId);
    if (dataTable?.[indexUpdate]?.details?.length > 0) return;
    const res = await getInventoryAuditDetailUseCase({
      auditId: auditId,
      setLoading,
      showError,
    });
    if (res && res?.length > 0) {
      setDataTable([
        ...dataTable.slice(0, indexUpdate),
        {
          ...dataTable[indexUpdate],
          details: res,
        },
        ...dataTable.slice(indexUpdate + 1),
      ]);
    }
  }

  const expandedRowRender = (record: InventoryAuditModel) => {
    if (!record.details || record.details.length === 0) {
      return <span style={{ paddingLeft: 16 }}>Không có dữ liệu</span>;
    }
    const nestedColumns: ColumnsType<InventoryAuditDetailModel> = [
      {
        title: "Nguyên vật liệu",
        dataIndex: "rawName",
        key: "rawName",
      },
      {
        title: "Số lượng",
        dataIndex: "quantity",
        key: "quantity",
        render(value, record, index) {
          return `${value}  (${record.uomName})`;
        },
      },
      {
        title: "Giá thành",
        dataIndex: "unitPrice",
        key: "unitPrice",
        render(value, record, index) {
          return currencyFormat(value);
        },
      },

      {
        title: "Giá trị",
        dataIndex: "subTotal",
        key: "subTotal",
        render(value, record, index) {
          return currencyFormat(value);
        },
      },
    ];
    return (
      <Table
        scroll={{
          y: "300px",
        }}
        columns={nestedColumns}
        dataSource={record.details}
        pagination={false}
        rowKey="id"
      />
    );
  };
  useAsyncEffect(async () => {
    await Promise.all([getListData()]);
  }, [activeTab]);

  return (
    <Column className="h-100 w-100">
      <Column className="gap-2 pb-2">
        <ActionHeader
          rowCount={total}
          actions={[
            {
              icon: <ReloadOutlined />,
              userClick: getListData,
              title: "Làm mới",
              isNormal: true,
            },
            {
              icon: <DeleteOutlined />,
              userClick: confirmDelete,
              title: "Xóa",
              disabled: !itemSelected?.id,
              isNormal: true,
              color: "danger",
            },
          ]}
        />
        <Row className="w-100 gap-2 p-1">
          <DateRangeSelectorComponent />

          <Column style={{ paddingTop: "25px" }}>
            <Button
              icon={<SearchOutlined />}
              type="primary"
              onClick={() => getListData()}
              className="w-100"
            >
              Tìm kiếm
            </Button>
          </Column>
        </Row>
        {/* Danh sách Table */}
      </Column>

      <BaseTable
        dataSource={dataTable}
        rowSelection={{
          type: "radio",
          selectedRowKeys: itemSelected?.id ? [itemSelected.id] : [],
          onSelect(record) {
            setItemSelected(record);
          },
        }}
        loading={isLoading}
        columns={[
          {
            title: "Ngày kiểm kê kho",
            dataIndex: "auditDate",
            key: "auditDate",
            render(value, record, index) {
              return new Date(value).toLocaleDateString("vi-VN");
            },
          },
          {
            title: "Tổng giá trị tồn",
            dataIndex: "totalValue",
            key: "totalValue",
            render(value, record, index) {
              return currencyFormat(value);
            },
          },
          {
            title: "Ghi chú",
            dataIndex: "note",
            key: "note",
          },
        ]}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              // Thêm key nếu chưa có
              setExpandedRowKeys((prev) => [...prev, record.id]);
              getInventoryAuditDetail(record.id); // Gọi dữ liệu khi mở
            } else {
              // Loại bỏ key khi đóng
              setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
            }
          },
        }}
        rowKey="id"
      />
      {confirmDeleteDialog}
    </Column>
  );
}

export default InventoryCheckHistory;
